/**
 * The undrawable-corridor warning (D175): a room drawn as a corridor whose
 * links do not run along one axis is drawn as a room, and the author
 * should hear so from the loader rather than from the map.
 *
 * @remarks
 * The hologram (D169) turns a corridor along its links — long along the
 * axis they share, low — and when the links do not share an axis (two
 * axes, a diagonal, or none at all: a room reached only by up and down),
 * it falls back to drawing the box a `room` gets. That fallback is right
 * and silent, which is the problem: the author wrote `kind: 'corridor'`,
 * the map ignored it, and nothing said so. This is the loader's half of
 * the same rule, judged statically from the exits — the room's own, and
 * every other room's that leads into it, since a link is drawn from both
 * ends — through the engine's own reading of which axis a direction runs
 * along, so the two halves cannot disagree.
 *
 * A warning, never a failure: the map is a presentation choice and the
 * room plays the same either way.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import { mapDirectionAxisOf } from '@orpheus/engine-runtime';

/** One corridor the map cannot draw as one. */
export interface MapKindUndrawable {
	/** The directions its links run in, as authored, in document order — the evidence. */
	readonly directionTokens: readonly string[];
	readonly roomId: string;
}

/**
 * Every room marked `kind: 'corridor'` whose links do not all run along one
 * axis of the floor, in document order.
 *
 * @param contentPack - The parsed pack
 * @returns The corridors the map will draw as rooms
 */
export const findUndrawableCorridors = (contentPack: ContentPackDefinition): readonly MapKindUndrawable[] =>
	contentPack.rooms
		.filter((room) => room.map?.kind === 'corridor')
		.flatMap((room) => {
			const own = room.exits.map((exit) => exit.directionToken);
			const inbound = contentPack.rooms.flatMap((other) =>
				other.roomId === room.roomId ? [] : other.exits.filter((exit) => exit.targetRoomId === room.roomId).map((exit) => exit.directionToken),
			);
			const directionTokens = [...own, ...inbound];
			const axes = directionTokens.map(mapDirectionAxisOf).filter((axis) => axis !== undefined);
			const [first] = axes;
			const isAlongOneAxis = first !== undefined && first !== 'diagonal' && axes.every((axis) => axis === first);

			return isAlongOneAxis ? [] : [{ directionTokens, roomId: room.roomId }];
		});
